import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    FormHelperText,
    TextField,
    Typography,
    MenuItem,
    Checkbox,
    FormControlLabel,
    IconButton,
    Stack,
    Button
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import AdmicityGroupFilterSelect from '../../../../shared-components/AdmicityGroupFilterSelect';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import {validateFieldLength} from '../../../../utility/validationUtil';
import {useGetFormCategoriesQuery} from '../../../../api/services/formCategoriesService';
import PropTypes from 'prop-types';
import {useConfirm} from 'material-ui-confirm';
import {needsNoCategoryWarning} from './createGroupFromResponsesRules';
import AdmicityTabs from '../../../../shared-components/AdmicityTabs';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import {
    buildExportToGroupSelectableSchema,
    buildQuestionAnswersFromExportRow,
    determineExportToGroupOperations,
    filterGroupModulesByMembersType
} from './exportToGroupUtils';

const getLinkedFormCriteria = (group) => {
    const list = group?.linkedFormFilters ?? group?.linkedForms;
    if (Array.isArray(list) && list.length > 0) return list;
    if (group?.linkedForm) return [group.linkedForm];
    return [];
};

const getExistingGroupExportError = (groups, row, rowIndex, selectedRows) => {
    if (!row.existingGroupFilterId || row.component) return null;

    if (selectedRows.some((other, index) =>
        index !== rowIndex &&
        other.existingGroupFilterId === row.existingGroupFilterId &&
        !other.component)) {
        return 'All responses is already selected for this group in another row.';
    }

    const linkedGroup = (groups ?? []).find(g => g.groupFilterId === row.existingGroupFilterId);
    if (!linkedGroup) return null;

    const criteria = getLinkedFormCriteria(linkedGroup);
    if (criteria.length === 0) {
        return 'This group is already linked to this form for all responses. Choose a question filter or a different group.';
    }

    return null;
};

const CreateGroupFromResponsesDialog = ({
    open,
    title,
    onClose,
    onCreateGroup,
    onExtendGroup,
    schema,
    lookups,
    groupFilters,
    groups,
    membersType
}) => {
    const {data: formCategories} = useGetFormCategoriesQuery();
    const confirm = useConfirm();
    const [selectedRows, setSelectedRows] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [parentGroupFilterId, setParentGroupFilterId] = useState('');
    const [isGroupDynamic, setIsGroupDynamic] = useState(true);
    const [addToParentGroups, setAddToParentGroups] = useState(false);
    const memberTypeLabels = {
        [GROUP_MEMBERS_TYPE.students]: {
            singular: 'Student',
            plural: 'Students',
        },
        [GROUP_MEMBERS_TYPE.employee]: {
            singular: 'Employee',
            plural: 'Employees',
        },
        [GROUP_MEMBERS_TYPE.none]: {
            singular: 'Profile',
            plural: 'Profiles',
        }
    };
    const {plural: memberTypeTitlePlural} =
        memberTypeLabels[membersType] ?? memberTypeLabels[GROUP_MEMBERS_TYPE.none];
    const filteredGroupFilters = filterGroupModulesByMembersType(groupFilters?.modules, membersType);

    const findGroupLevel = (groupId) => {
        if (!groupFilters?.modules) return null;
        for (const module of groupFilters.modules) {
            for (const filter of module.filters) {
                if (filter.id === groupId) return filter.level;
            }
        }
        return null;
    };

    const getEmptyNewRow = () => ({
        component: null,
        targetState: null,
        groupName: '',
        formCategoryId: null
    });

    const getEmptyExistingRow = () => ({
        component: null,
        targetState: null,
        existingGroupFilterId: ''
    });

    useEffect(() => {
        if (!open) {
            setActiveTab(0);
            setSelectedRows([getEmptyNewRow()]);
            setParentGroupFilterId('');
            setIsGroupDynamic(true);
            setAddToParentGroups(false);
        }
    }, [open]);

    useEffect(() => {
        if (open && selectedRows.length === 0) {
            setSelectedRows(activeTab === 0 ? [getEmptyNewRow()] : [getEmptyExistingRow()]);
        }
    }, [activeTab, open]);

    const addRow = () => {
        setSelectedRows(prev => [...prev, activeTab === 0 ? getEmptyNewRow() : getEmptyExistingRow()]);
    };

    const updateRow = (index, updates) => {
        setSelectedRows(prev => {
            const next = [...prev];
            next[index] = { ...next[index], ...updates };
            if (updates.component !== undefined) next[index].targetState = null;
            return next;
        });
    };

    const removeRow = (index) => {
        setSelectedRows(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        const shouldApplyAddToParentGroups = activeTab === 0
            ? shouldShowCreateParentGroupsOption
            : shouldShowExistingParentGroupsOption;

        if (activeTab === 0) {
            // AD-2407: a new top-level group without a category asks first, as on the Profiles page (AdmicityDrawer).
            if (needsNoCategoryWarning(parentGroupFilterId, selectedRows)) {
                try {
                    await confirm({
                        title: 'Save group without category?',
                        description: 'This top-level group does not have a category assigned. Do you want to continue saving it without a category?',
                        confirmationText: 'Save',
                        cancellationText: 'Cancel',
                    });
                } catch {
                    return;
                }
            }
            const groups = selectedRows.map(row => ({
                groupName: row.groupName,
                formCategoryId: row.formCategoryId,
                questionAnswers: buildQuestionAnswersFromExportRow(row.component, row.targetState)
            }));
            await onCreateGroup({
                parentGroupFilterId: parentGroupFilterId || null,
                isGroupDynamic,
                addToParentGroups: shouldApplyAddToParentGroups && addToParentGroups,
                groups
            });
        } else {
            const operations = selectedRows.map(row => ({
                existingGroupFilterId: row.existingGroupFilterId,
                questionAnswers: buildQuestionAnswersFromExportRow(row.component, row.targetState)
            }));
            await onExtendGroup({
                operations,
                addToParentGroups: shouldApplyAddToParentGroups && addToParentGroups
            });
        }
    };

    const hasSelectedParentGroup = Boolean(parentGroupFilterId);
    const selectedParentGroupLevel = findGroupLevel(parentGroupFilterId);
    const isSelectedParentSubgroup = selectedParentGroupLevel > 2;
    const shouldShowCreateParentGroupsOption = activeTab === 0;
    const shouldShowExistingParentGroupsOption = activeTab === 1 &&
        selectedRows.some(row => findGroupLevel(row.existingGroupFilterId) > 2);

    useEffect(() => {
        if (!shouldShowCreateParentGroupsOption && !shouldShowExistingParentGroupsOption && addToParentGroups) {
            setAddToParentGroups(false);
        }
    }, [addToParentGroups, shouldShowCreateParentGroupsOption, shouldShowExistingParentGroupsOption]);

    const rowsWithExportAll = selectedRows.filter(
        r => !r.component || r.targetState == null
    );
    const allRowsHaveQuestion = selectedRows.length > 0 &&
        selectedRows.every(r => !!r.component && r.targetState != null);
    const singleRowExportAll = selectedRows.length === 1 && !selectedRows[0].component;
    const noMixValid = singleRowExportAll || allRowsHaveQuestion;

    const isNewGroupRowValid = (row) => {
        const nameOk = row.groupName && validateFieldLength(row.groupName, 255).isValid;
        const hasQuestion = !!row.component && row.targetState != null;
        const isExportAll = !row.component;
        return nameOk && (hasQuestion || (isExportAll && singleRowExportAll));
    };

    const selectableSchema = buildExportToGroupSelectableSchema(schema);

    const isExistingGroupRowValid = (row, index) => {
        const groupOk = Boolean(row.existingGroupFilterId);
        const hasQuestion = !!row.component && row.targetState != null;
        const isExportAll = !row.component;
        const noDuplicateExport = !getExistingGroupExportError(groups, row, index, selectedRows);
        return groupOk && noDuplicateExport && (hasQuestion || (isExportAll && singleRowExportAll));
    };

    const isNewGroupTabValid = () =>
        selectedRows.length > 0 &&
        selectedRows.every(isNewGroupRowValid) &&
        noMixValid;

    const isExistingGroupTabValid = () =>
        selectedRows.length > 0 &&
        selectedRows.every((row, index) => isExistingGroupRowValid(row, index)) &&
        noMixValid;

    const showMixError = selectedRows.length > 1 && rowsWithExportAll.length > 0;

    const renderNewGroupRows = () => (
        <Stack spacing={2}>
            <Box>
                <Typography variant="subtitle1" fontWeight="bold">Parent Group</Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    Optionally place this new group under an existing parent group.
                </Typography>
                <AdmicityGroupFilterSelect
                    sx={{ width: '100%' }}
                    data={filteredGroupFilters}
                    value={parentGroupFilterId}
                    onChange={(nodeId) => {
                        setParentGroupFilterId(nodeId);

                        if (!nodeId) {
                            setAddToParentGroups(false);
                        }
                    }}
                    isRequired={false}
                    label="Parent group (optional)"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isGroupDynamic}
                            onChange={(e) => setIsGroupDynamic(e.target.checked)}
                        />
                    }
                    sx={{ mt: 1 }}
                    label={`Dynamic Groups (auto-includes ${memberTypeTitlePlural.toLowerCase()})`}
                />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: -0.5 }}>
                    Automatically add {memberTypeTitlePlural.toLowerCase()} to this group when future form responses
                    match these conditions.
                </Typography>
                {shouldShowCreateParentGroupsOption && (
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={addToParentGroups}
                                    onChange={(e) => setAddToParentGroups(e.target.checked)}
                                    disabled={!hasSelectedParentGroup || !isSelectedParentSubgroup}
                                />
                            }
                            label="Export to parent group(s)"
                        />
                    <FormHelperText sx={{ mt: -0.5 }}>
                        Export profiles to all parent groups of this subgroup.
                    </FormHelperText>
                    </>
                )}
                {isSelectedParentSubgroup && !addToParentGroups && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Only profiles that exist in the parent group(s) will be exported to this subgroup.
                    </Alert>
                )}
            </Box>
            {showMixError && (
                <Alert severity="error">
                    Either export all responses to one group (use a single row with no question) or create multiple
                    groups with a question selected for each. Mixing is not allowed.
                </Alert>
            )}
            <Typography variant="subtitle2" color="text.secondary">
                Leave Question and Equals blank to export all {memberTypeTitlePlural.toLowerCase()} from form
                responses.
            </Typography>
            {selectedRows.map((row, index) => (
                <Box key={index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <TextField
                                    required
                                    size="small"
                                    label="Group Name"
                                    value={row.groupName}
                                    error={!!row.groupName && !validateFieldLength(row.groupName, 255).isValid}
                                    helperText={row.groupName ? validateFieldLength(row.groupName, 255).message : ''}
                                    onChange={e => updateRow(index, { groupName: e.target.value })}
                                    inputProps={{ maxLength: 255 }}
                                    sx={{ minWidth: 180, flex: 1 }}
                                />
                                <TextField
                                    select
                                    size="small"
                                    label="Category"
                                    value={row.formCategoryId || ''}
                                    onChange={e => updateRow(index, { formCategoryId: e.target.value || null })}
                                    sx={{ minWidth: 160 }}
                                    SelectProps={{
                                        renderValue: (selected) => {
                                            if (!selected) return 'No category';
                                            const cat = formCategories?.find(c => c.id === selected);
                                            return cat ? cat.name : '';
                                        }
                                    }}
                                >
                                    <MenuItem value=""><em>No category</em></MenuItem>
                                    {(formCategories || []).map(({ id, name }) => (
                                        <MenuItem key={id} value={id}>{name}</MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
                                <TextField
                                    select
                                    size="small"
                                    sx={{ flex: 1, minWidth: 200 }}
                                    value={row.component ? row.component.id : ''}
                                    label="Question"
                                    onChange={e => {
                                        const id = e.target.value;
                                        const comp = id
                                            ? selectableSchema.find(c => c.id === id || String(c.id) === String(id)) || null
                                            : null;
                                        updateRow(index, { component: comp });
                                    }}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: {
                                                sx: {maxWidth: 560},
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value=""><em>All responses</em></MenuItem>
                                    {selectableSchema.map(opt => (
                                        <MenuItem
                                            key={opt.id}
                                            value={opt.id}
                                            title={opt.description}
                                            sx={{whiteSpace: 'normal'}}
                                        >
                                            {opt.description}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    size="small"
                                    sx={{ flex: 1, minWidth: 160 }}
                                    value={row.targetState ?? ''}
                                    label="Equals"
                                    disabled={!row.component}
                                    onChange={e => updateRow(index, { targetState: e.target.value })}
                                >
                                    {row.component && determineExportToGroupOperations(
                                        row.component.lookupType
                                            ? { ...row.component, options: lookups[row.component.lookupType] || [] }
                                            : row.component,
                                        { checkboxOnlyChecked: true }
                                    )?.map(op => (
                                        <MenuItem key={op.value} value={op.value}>{op.title}</MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </Stack>
                        <IconButton
                            size="small"
                            onClick={() => removeRow(index)}
                            disabled={selectedRows.length <= 1}
                            aria-label="Remove group"
                            sx={{ flexShrink: 0 }}
                        >
                            <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addRow} disabled={!schema}>
                Add another group
            </Button>
        </Stack>
    );

    const renderExistingGroupRows = () => (
        <Stack spacing={2}>
            {shouldShowExistingParentGroupsOption && (
                <Box sx={{ m: 0, p: 0 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={addToParentGroups}
                                onChange={(e) => setAddToParentGroups(e.target.checked)}
                                sx={{ p: 0, mr: 1 }}
                            />
                        }
                        sx={{ m: 0, ml: 0 }}
                        label="Export to parent group(s)"
                    />
                    <FormHelperText sx={{ mt: 1, ml: 0 }}>
                        Export profiles to all parent groups of this subgroup.
                    </FormHelperText>
                </Box>
            )}
            {shouldShowExistingParentGroupsOption && !addToParentGroups && (
                <Alert severity="warning">
                    Only profiles that exist in the parent group(s) will be exported to this subgroup.
                </Alert>
            )}
            {showMixError && (
                <Alert severity="error">
                    Either export all responses to one group (use a single row with no question) or create multiple
                    groups with a question selected for each. Mixing is not allowed.
                </Alert>
            )}
            {selectedRows.map((row, index) => {
                const existingGroupExportError = getExistingGroupExportError(
                    groups,
                    row,
                    index,
                    selectedRows
                );

                return (
                <Box key={index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                            <AdmicityGroupFilterSelect
                                sx={{ width: '100%' }}
                                data={filteredGroupFilters}
                                value={row.existingGroupFilterId || ''}
                                onChange={nodeId => updateRow(index, { existingGroupFilterId: nodeId })}
                                isRequired
                                label="Existing group"
                            />
                            <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
                                <TextField
                                    select
                                    size="small"
                                    sx={{ flex: 1, minWidth: 200 }}
                                    value={row.component ? row.component.id : ''}
                                    label="Question"
                                    onChange={e => {
                                        const id = e.target.value;
                                        const comp = id
                                            ? selectableSchema.find(c => c.id === id || String(c.id) === String(id)) || null
                                            : null;
                                        updateRow(index, { component: comp });
                                    }}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: {
                                                sx: {maxWidth: 560},
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value=""><em>All responses</em></MenuItem>
                                    {selectableSchema.map(opt => (
                                        <MenuItem
                                            key={opt.id}
                                            value={opt.id}
                                            title={opt.description}
                                            sx={{whiteSpace: 'normal'}}
                                        >
                                            {opt.description}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    size="small"
                                    sx={{ flex: 1, minWidth: 160 }}
                                    value={row.targetState ?? ''}
                                    label="Equals"
                                    disabled={!row.component}
                                    onChange={e => updateRow(index, { targetState: e.target.value })}
                                >
                                    {row.component && determineExportToGroupOperations(
                                        row.component.lookupType
                                            ? { ...row.component, options: lookups[row.component.lookupType] || [] }
                                            : row.component,
                                        { checkboxOnlyChecked: true }
                                    )?.map(op => (
                                        <MenuItem key={op.value} value={op.value}>{op.title}</MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                            {existingGroupExportError && (
                                <Alert severity="error">{existingGroupExportError}</Alert>
                            )}
                        </Stack>
                        <IconButton
                            size="small"
                            onClick={() => removeRow(index)}
                            disabled={selectedRows.length <= 1}
                            aria-label="Remove"
                            sx={{ flexShrink: 0 }}
                        >
                            <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>
                );
            })}
            <Button size="small" startIcon={<AddIcon />} onClick={addRow} disabled={!schema}>
                Add another group
            </Button>
        </Stack>
    );

    const tabs = [
        {
            label: 'Export to New Group',
            component: (
                <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
                    {renderNewGroupRows()}
                </Box>
            ),
            isValid: () => isNewGroupTabValid()
        },
        {
            label: 'Export to Existing Group',
            component: (
                <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
                    {renderExistingGroupRows()}
                </Box>
            ),
            isValid: () => isExistingGroupTabValid()
        }
    ];

    const handleTabChange = (tabIndex) => {
        setActiveTab(tabIndex);
        setSelectedRows(tabIndex === 0 ? [getEmptyNewRow()] : [getEmptyExistingRow()]);
    };

    return (
        <AdmicityDialog
            handleClose={onClose}
            title={title}
            open={open}
            actions={[
                { label: 'Cancel', onClick: onClose },
                { label: 'Save', disabled: !tabs[activeTab]?.isValid(), onClick: handleSave }
            ]}
        >
            <Alert severity="info" sx={{ mb: 2 }}>
                {memberTypeTitlePlural} will be assigned to the group(s) based on the filters applied to the form responses.
            </Alert>
            <AdmicityTabs
                tabs={tabs}
                variant="fullWidth"
                tabIndex={activeTab}
                onChange={handleTabChange}
                slots={{ panel: { paddingRight: 0, paddingLeft: 0 } }}
            />
        </AdmicityDialog>
    );
};

CreateGroupFromResponsesDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.node.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreateGroup: PropTypes.func.isRequired,
    onExtendGroup: PropTypes.func.isRequired,
    schema: PropTypes.array,
    lookups: PropTypes.object,
    groupFilters: PropTypes.object,
    groups: PropTypes.array,
    membersType: PropTypes.number
};

export default CreateGroupFromResponsesDialog;
